export class PopupManager {
  private static instance: PopupManager;
  private activePopup: HTMLElement | null = null;

  private constructor() {}

  public static getInstance(): PopupManager {
    if (!PopupManager.instance) {
      PopupManager.instance = new PopupManager();
    }
    return PopupManager.instance;
  }

  public showAlert(message: string): Promise<void> {
    return new Promise((resolve) => {
      this.closeActivePopup();

      const overlay = this.createOverlay();
      const popup = this.createPopup();
      
      const content = document.createElement('div');
      content.style.cssText = `
        padding: 20px;
        text-align: center;
      `;
      
      const messageDiv = document.createElement('div');
      messageDiv.textContent = message;
      messageDiv.style.cssText = `
        margin-bottom: 20px;
        font-size: 16px;
        line-height: 1.5;
        color: #f0f0f0;
      `;
      content.appendChild(messageDiv);
      
      const okButton = this.createButton('OK', '#4a7c59');
      okButton.onclick = () => {
        this.closePopup(overlay);
        resolve();
      };
      content.appendChild(okButton);
      
      popup.appendChild(content);
      overlay.appendChild(popup);
      document.body.appendChild(overlay);
      
      this.activePopup = overlay;
      this.animateIn(overlay, popup);
    });
  }

  public showConfirm(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.closeActivePopup();

      const overlay = this.createOverlay();
      const popup = this.createPopup();
      
      const content = document.createElement('div');
      content.style.cssText = `
        padding: 20px;
        text-align: center;
      `;
      
      const messageDiv = document.createElement('div');
      messageDiv.textContent = message;
      messageDiv.style.cssText = `
        margin-bottom: 25px;
        font-size: 16px;
        line-height: 1.5;
        color: #f0f0f0;
      `;
      content.appendChild(messageDiv);
      
      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = `
        display: flex;
        gap: 15px;
        justify-content: center;
      `;
      
      const cancelButton = this.createButton('Cancel', '#666666');
      cancelButton.onclick = () => {
        this.closePopup(overlay);
        resolve(false);
      };
      
      const confirmButton = this.createButton('Confirm', '#8b2222');
      confirmButton.onclick = () => {
        this.closePopup(overlay);
        resolve(true);
      };
      
      buttonContainer.appendChild(cancelButton);
      buttonContainer.appendChild(confirmButton);
      content.appendChild(buttonContainer);
      
      popup.appendChild(content);
      overlay.appendChild(popup);
      document.body.appendChild(overlay);
      
      this.activePopup = overlay;
      this.animateIn(overlay, popup);
    });
  }

  public showInfo(message: string, _duration: number = 3000): void {
    // Check if notifications are enabled
    const notificationsEnabled = localStorage.getItem('notificationsEnabled') !== 'false';
    if (!notificationsEnabled) {
      return;
    }
    
    // Get notification duration from settings
    const savedDuration = localStorage.getItem('notificationDuration') || '10';
    const actualDuration = savedDuration === 'manual' ? -1 : parseInt(savedDuration) * 1000;
    
    // Create a non-blocking info popup container
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      top: 100px;
      left: 50%;
      transform: translateX(-50%) translateY(-20px);
      z-index: 40000;
      opacity: 0;
      transition: all 0.3s ease;
    `;
    
    // Create the main popup
    const infoPopup = document.createElement('div');
    infoPopup.style.cssText = `
      background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
      border: 2px solid #4a4a4a;
      border-radius: 8px;
      padding: 15px 25px;
      color: #f0f0f0;
      font-size: 16px;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.6);
      max-width: 400px;
      text-align: center;
      display: flex;
      align-items: center;
      gap: 15px;
      position: relative;
    `;
    
    // Add message text
    const messageSpan = document.createElement('span');
    messageSpan.textContent = message;
    messageSpan.style.flex = '1';
    infoPopup.appendChild(messageSpan);
    
    // Add close button for manual dismissal
    const closeButton = document.createElement('button');
    closeButton.textContent = '✕';
    closeButton.style.cssText = `
      background: transparent;
      border: none;
      color: #888;
      font-size: 20px;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.2s ease;
    `;
    
    closeButton.onmouseover = () => {
      closeButton.style.color = '#fff';
    };
    
    closeButton.onmouseout = () => {
      closeButton.style.color = '#888';
    };
    
    const dismissNotification = () => {
      container.style.opacity = '0';
      container.style.transform = 'translateX(-50%) translateY(-20px)';
      setTimeout(() => {
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      }, 300);
    };
    
    closeButton.onclick = dismissNotification;
    
    infoPopup.appendChild(closeButton);
    container.appendChild(infoPopup);
    
    // Add progress slider if not in manual mode
    if (actualDuration > 0) {
      const progressContainer = document.createElement('div');
      progressContainer.style.cssText = `
        width: 100%;
        height: 4px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 2px;
        margin-top: 8px;
        overflow: hidden;
      `;
      
      const progressBar = document.createElement('div');
      progressBar.style.cssText = `
        height: 100%;
        width: 100%;
        background: linear-gradient(90deg, #4a7c59, #6fa382);
        border-radius: 2px;
        transition: transform ${actualDuration}ms linear;
        transform-origin: left center;
      `;
      
      progressContainer.appendChild(progressBar);
      container.appendChild(progressContainer);
      
      // Start the progress animation after the popup appears
      setTimeout(() => {
        progressBar.style.transform = 'scaleX(0)';
      }, 350);
      
      // Auto dismiss after duration
      setTimeout(() => {
        if (document.body.contains(container)) {
          dismissNotification();
        }
      }, actualDuration);
    }
    
    document.body.appendChild(container);
    
    // Animate in
    requestAnimationFrame(() => {
      container.style.opacity = '1';
      container.style.transform = 'translateX(-50%) translateY(0)';
    });
  }

  private createOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      z-index: 35000;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    return overlay;
  }

  private createPopup(): HTMLElement {
    const popup = document.createElement('div');
    popup.style.cssText = `
      background: linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%);
      border: 3px solid #5a5a5a;
      border-radius: 10px;
      min-width: 300px;
      max-width: 500px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.8);
      transform: scale(0.9);
      transition: transform 0.3s ease;
    `;
    return popup;
  }

  private createButton(text: string, bgColor: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.textContent = text;
    button.style.cssText = `
      background: ${bgColor};
      color: #f0f0f0;
      border: 2px solid ${this.darkenColor(bgColor)};
      padding: 10px 25px;
      font-size: 14px;
      font-weight: bold;
      border-radius: 5px;
      cursor: pointer;
      text-transform: uppercase;
      letter-spacing: 1px;
      transition: all 0.2s ease;
      box-shadow: 0 3px 5px rgba(0, 0, 0, 0.3);
    `;
    
    button.onmouseover = () => {
      button.style.background = this.lightenColor(bgColor);
      button.style.transform = 'translateY(-1px)';
      button.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.4)';
    };
    
    button.onmouseout = () => {
      button.style.background = bgColor;
      button.style.transform = 'translateY(0)';
      button.style.boxShadow = '0 3px 5px rgba(0, 0, 0, 0.3)';
    };
    
    return button;
  }

  private animateIn(overlay: HTMLElement, popup: HTMLElement): void {
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      popup.style.transform = 'scale(1)';
    });
  }

  private closePopup(overlay: HTMLElement): void {
    const popup = overlay.querySelector('div') as HTMLElement;
    if (popup) {
      popup.style.transform = 'scale(0.9)';
    }
    overlay.style.opacity = '0';
    
    setTimeout(() => {
      document.body.removeChild(overlay);
      if (this.activePopup === overlay) {
        this.activePopup = null;
      }
    }, 300);
  }

  private closeActivePopup(): void {
    if (this.activePopup) {
      this.closePopup(this.activePopup);
    }
  }

  private darkenColor(color: string): string {
    // Simple color darkening
    const num = parseInt(color.slice(1), 16);
    const r = Math.max(0, (num >> 16) - 30);
    const g = Math.max(0, ((num >> 8) & 0x00FF) - 30);
    const b = Math.max(0, (num & 0x0000FF) - 30);
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  }

  private lightenColor(color: string): string {
    // Simple color lightening
    const num = parseInt(color.slice(1), 16);
    const r = Math.min(255, (num >> 16) + 30);
    const g = Math.min(255, ((num >> 8) & 0x00FF) + 30);
    const b = Math.min(255, (num & 0x0000FF) + 30);
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  }

  public showGuide(): void {
    this.closeActivePopup();

    // Detect mobile
    const isMobile = window.innerWidth <= 812 || window.innerHeight <= 812;
    const isSmallScreen = window.innerWidth <= 480;

    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.85);
      z-index: 35000;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      ${isMobile ? 'padding: 0;' : ''}
    `;

    const guideContainer = document.createElement('div');
    guideContainer.style.cssText = `
      background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
      border: ${isMobile ? '0' : '3px solid #5a5a5a'};
      border-radius: ${isMobile ? '0' : '15px'};
      width: ${isMobile ? '100%' : '90%'};
      max-width: ${isMobile ? '100%' : '1200px'};
      height: ${isMobile ? '100%' : '85vh'};
      max-height: ${isMobile ? '100%' : '800px'};
      box-shadow: ${isMobile ? 'none' : '0 15px 40px rgba(0, 0, 0, 0.9)'};
      display: flex;
      flex-direction: column;
      transform: scale(${isMobile ? '1' : '0.9'});
      transition: transform 0.3s ease;
      position: ${isMobile ? 'fixed' : 'relative'};
      top: ${isMobile ? '0' : 'auto'};
      left: ${isMobile ? '0' : 'auto'};
      right: ${isMobile ? '0' : 'auto'};
      bottom: ${isMobile ? '0' : 'auto'};
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      background: linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%);
      padding: ${isMobile ? '10px 12px' : '20px 30px'};
      border-bottom: 2px solid #5a5a5a;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;
      min-height: ${isMobile ? '50px' : 'auto'};
      gap: 10px;
    `;

    const title = document.createElement('h2');
    title.textContent = isMobile ? 'GUIDE' : 'FRONTLINES WW2 - GAME GUIDE';
    title.style.cssText = `
      color: #f0f0f0;
      font-size: ${isSmallScreen ? '14px' : isMobile ? '16px' : '28px'};
      margin: 0;
      text-transform: uppercase;
      letter-spacing: ${isMobile ? '0.5px' : '2px'};
      text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `;
    header.appendChild(title);

    const closeButton = document.createElement('button');
    closeButton.textContent = '✕';
    closeButton.style.cssText = `
      background: #8b2222;
      border: 2px solid #6b1212;
      color: white;
      font-size: ${isMobile ? '16px' : '20px'};
      width: ${isMobile ? '32px' : '40px'};
      height: ${isMobile ? '32px' : '40px'};
      min-width: ${isMobile ? '32px' : '40px'};
      min-height: ${isMobile ? '32px' : '40px'};
      border-radius: 5px;
      cursor: pointer;
      transition: all 0.2s ease;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    `;
    closeButton.onmouseover = () => {
      closeButton.style.background = '#ab3232';
      closeButton.style.transform = 'scale(1.1)';
    };
    closeButton.onmouseout = () => {
      closeButton.style.background = '#8b2222';
      closeButton.style.transform = 'scale(1)';
    };
    closeButton.onclick = () => this.closePopup(overlay);
    header.appendChild(closeButton);

    // Tabs
    const tabContainer = document.createElement('div');
    tabContainer.style.cssText = `
      display: flex;
      background: #1a1a1a;
      border-bottom: 2px solid #4a4a4a;
      ${isMobile ? 'overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none;' : ''}
      flex-shrink: 0;
      min-height: ${isMobile ? '40px' : 'auto'};
    `;
    
    // Hide scrollbar on mobile
    if (isMobile) {
      // Add style to hide scrollbar
      const style = document.createElement('style');
      style.textContent = `
        #game-guide-tabs::-webkit-scrollbar {
          display: none;
        }
        #game-guide-tabs {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `;
      document.head.appendChild(style);
      tabContainer.id = 'game-guide-tabs';
    }

    const tabs = isMobile ? 
      ['Overview', 'Controls', 'Terrain', 'Strategy', 'Units'] : 
      ['Overview', 'Controls', 'Terrain', 'Strategy', 'Factions', 'Units', 'Game Modes'];
    const tabButtons: HTMLButtonElement[] = [];
    const tabContents: HTMLElement[] = [];

    tabs.forEach((tabName, index) => {
      const tabButton = document.createElement('button');
      tabButton.textContent = tabName;
      tabButton.style.cssText = `
        ${isMobile ? 'flex: 1; min-width: 0;' : 'flex: 1;'}
        padding: ${isMobile ? '8px 6px' : '15px'};
        background: ${index === 0 ? '#3a3a3a' : '#2a2a2a'};
        border: none;
        border-right: ${index === tabs.length - 1 ? 'none' : '1px solid #4a4a4a'};
        color: ${index === 0 ? '#f0f0f0' : '#aaa'};
        font-size: ${isSmallScreen ? '10px' : isMobile ? '11px' : '16px'};
        cursor: pointer;
        transition: all 0.2s ease;
        text-transform: uppercase;
        letter-spacing: ${isMobile ? '0' : '1px'};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `;
      
      tabButton.onclick = () => {
        tabButtons.forEach((btn, i) => {
          btn.style.background = i === index ? '#3a3a3a' : '#2a2a2a';
          btn.style.color = i === index ? '#f0f0f0' : '#aaa';
        });
        tabContents.forEach((content, i) => {
          content.style.display = i === index ? 'block' : 'none';
        });
      };
      
      tabButtons.push(tabButton);
      tabContainer.appendChild(tabButton);
    });

    // Content area
    const contentArea = document.createElement('div');
    contentArea.style.cssText = `
      flex: 1;
      padding: ${isMobile ? '15px' : '20px'};
      overflow-y: auto;
      color: #e0e0e0;
      -webkit-overflow-scrolling: touch;
      min-height: 0;
    `;

    // Create content for each tab
    const overviewContent = this.createOverviewContent(isMobile);
    const controlsContent = this.createControlsContent(isMobile);
    const terrainContent = this.createTerrainContent(isMobile);
    const strategyContent = this.createStrategyContent(isMobile);
    const unitsContent = this.createUnitsContent(isMobile);
    
    if (!isMobile) {
      const factionsContent = this.createFactionsContent(isMobile);
      const gameModesContent = this.createGameModesContent(isMobile);
      tabContents.push(overviewContent, controlsContent, terrainContent, strategyContent, factionsContent, unitsContent, gameModesContent);
    } else {
      tabContents.push(overviewContent, controlsContent, terrainContent, strategyContent, unitsContent);
    }
    
    tabContents.forEach((content, index) => {
      content.style.display = index === 0 ? 'block' : 'none';
      contentArea.appendChild(content);
    });

    guideContainer.appendChild(header);
    guideContainer.appendChild(tabContainer);
    guideContainer.appendChild(contentArea);
    overlay.appendChild(guideContainer);
    document.body.appendChild(overlay);

    this.activePopup = overlay;
    
    // Animate in
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      guideContainer.style.transform = 'scale(1)';
    });
  }

  private createOverviewContent(isMobile: boolean = false): HTMLElement {
    const content = document.createElement('div');
    content.innerHTML = `
      <h3 style="color: #f0f0f0; margin-bottom: 20px; font-size: ${isMobile ? '20px' : '24px'};">Welcome to Frontlines WW2</h3>
      
      <div style="margin-bottom: 25px; line-height: 1.8; font-size: 16px;">
        <p style="margin-bottom: 15px;">
          <strong style="color: #f0f0f0;">Frontlines WW2</strong> is a comprehensive turn-based strategy game set during World War II. 
          Command historically accurate armies across six major campaigns spanning different theaters of the war, from the beaches of Normandy to the frozen streets of Stalingrad.
        </p>
        
        <div style="padding: 15px; background: rgba(255,215,0,0.1); border-left: 4px solid #ffd700; border-radius: 4px; margin: 20px 0;">
          <h4 style="color: #ffd700; margin: 0 0 10px 0; font-size: 18px;">🎯 Quick Start Tip</h4>
          <p style="margin: 0;">
            Focus on capturing cities early in the game. Cities provide resources and allow you to build new units. The more cities you control, the stronger your economy becomes!
          </p>
        </div>
        
        <h4 style="color: #f0f0f0; margin: 20px 0 10px 0; font-size: 20px;">Victory Conditions</h4>
        <p style="margin-bottom: 15px;">
          <strong>Total Victory:</strong> Control all cities on the map to win. Each city you capture strengthens your economy while weakening your opponent's ability to produce new units.
        </p>
        
        <h4 style="color: #f0f0f0; margin: 20px 0 10px 0; font-size: 20px;">Core Game Features</h4>
        <ul style="margin-left: 20px; list-style-type: disc;">
          <li style="margin-bottom: 8px;"><strong>Six Historical Campaigns:</strong> Normandy, Tunisia, Stalingrad, Okinawa, Campobasso, and Battle of France</li>
          <li style="margin-bottom: 8px;"><strong>Eight Unique Factions:</strong> USA, Germany, UK, USSR, Japan, Canada, Italy, and France</li>
          <li style="margin-bottom: 8px;"><strong>250+ Unit Types:</strong> Infantry, armor, artillery, aircraft, and specialized support units</li>
          <li style="margin-bottom: 8px;"><strong>Three-Tier City System:</strong> Outpost (3 units) → Factory (5 units) → Airbase (8 units)</li>
          <li style="margin-bottom: 8px;"><strong>Dynamic Terrain:</strong> Easy/Rough/Challenging terrain affecting movement and defense</li>
          <li style="margin-bottom: 8px;"><strong>Fog of War:</strong> Strategic reconnaissance and hidden unit positioning</li>
          <li style="margin-bottom: 8px;"><strong>Veteran System:</strong> Units gain ★ status and +25% stat bonuses after 3 kills</li>
          <li style="margin-bottom: 8px;"><strong>Combined Arms:</strong> Transport operations, airborne drops, artillery support</li>
          <li style="margin-bottom: 8px;"><strong>Special Abilities:</strong> Mine placement, healing, repair, hunker down</li>
          <li style="margin-bottom: 8px;"><strong>Multiplayer Modes:</strong> Local PvP, AI skirmish, and online multiplayer</li>
        </ul>
        
        <h4 style="color: #f0f0f0; margin: 20px 0 10px 0; font-size: 20px;">Historical Authenticity</h4>
        <p style="margin-bottom: 15px;">
          Experience pivotal battles from 1942-1945 with historically accurate unit types, costs, and capabilities. Each faction reflects real military doctrines and equipment, from American combined arms to German technological superiority to Soviet mass production capabilities.
        </p>
        
        <h4 style="color: #f0f0f0; margin: 20px 0 10px 0; font-size: 20px;">Resource Management</h4>
        <p style="margin-bottom: 15px;">
          Cities generate 5/10/15 resources per turn based on their level. Use resources to train new units, upgrade cities, and support special abilities like mine placement and unit healing. Strategic resource management is key to victory.
        </p>
      </div>
    `;
    return content;
  }

  private createControlsContent(isMobile: boolean = false): HTMLElement {
    const content = document.createElement('div');
    content.innerHTML = `
      <h3 style="color: #f0f0f0; margin-bottom: 20px; font-size: ${isMobile ? '20px' : '24px'};">Complete Controls Guide</h3>
      
      <div style="display: ${isMobile ? 'block' : 'grid'}; grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'}; gap: 25px;">
        <div>
          <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 18px;">🎮 Basic Controls</h4>
          <table style="width: 100%; border-collapse: collapse; background: rgba(255,255,255,0.02);">
            <tr style="border-bottom: 1px solid #4a4a4a;">
              <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">Left Click</td>
              <td style="padding: 12px;">Select unit or tile</td>
            </tr>
            <tr style="border-bottom: 1px solid #4a4a4a;">
              <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">Right Click</td>
              <td style="padding: 12px;">Deselect or cancel action</td>
            </tr>
            <tr style="border-bottom: 1px solid #4a4a4a;">
              <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">Second Click</td>
              <td style="padding: 12px;">Move unit or attack enemy</td>
            </tr>
            <tr>
              <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">END TURN Button</td>
              <td style="padding: 12px;">End your turn (UI button)</td>
            </tr>
          </table>
        </div>
        
        <div>
          <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 18px;">📷 Camera Controls</h4>
          <table style="width: 100%; border-collapse: collapse; background: rgba(255,255,255,0.02);">
            <tr style="border-bottom: 1px solid #4a4a4a;">
              <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">W/A/S/D</td>
              <td style="padding: 12px;">Pan camera</td>
            </tr>
            <tr style="border-bottom: 1px solid #4a4a4a;">
              <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">Arrow Keys</td>
              <td style="padding: 12px;">Pan camera</td>
            </tr>
            <tr style="border-bottom: 1px solid #4a4a4a;">
              <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">Mouse Wheel</td>
              <td style="padding: 12px;">Zoom in/out</td>
            </tr>
            <tr>
              <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">+/- Keys</td>
              <td style="padding: 12px;">Zoom in/out</td>
            </tr>
          </table>
        </div>
      </div>
      
      <div style="margin-top: 30px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 18px;">🏘️ City Management</h4>
        <table style="width: 100%; border-collapse: collapse; background: rgba(255,255,255,0.02);">
          <tr style="border-bottom: 1px solid #4a4a4a;">
            <td style="padding: 12px; width: 25%; color: #f0f0f0; font-weight: bold;">Click City</td>
            <td style="padding: 12px;">Open build menu (if friendly) or view city information</td>
          </tr>
          <tr style="border-bottom: 1px solid #4a4a4a;">
            <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">UPGRADE Button</td>
            <td style="padding: 12px;">Upgrade city level (25 resources: Outpost→Factory, 50 resources: Factory→Airbase)</td>
          </tr>
          <tr style="border-bottom: 1px solid #4a4a4a;">
            <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">Unit Selection</td>
            <td style="padding: 12px;">Choose unit to train (availability depends on city level)</td>
          </tr>
          <tr>
            <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">Capture</td>
            <td style="padding: 12px;">Move any unit into enemy/neutral city to begin capture</td>
          </tr>
        </table>
      </div>
      
      <div style="margin-top: 30px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 18px;">⚔️ Special Unit Actions</h4>
        <table style="width: 100%; border-collapse: collapse; background: rgba(255,255,255,0.02);">
          <tr style="border-bottom: 1px solid #4a4a4a;">
            <td style="padding: 12px; width: 25%; color: #f0f0f0; font-weight: bold;">HUNKER Button</td>
            <td style="padding: 12px;">+2 defense, heal 2 HP/turn, unit cannot move (any unit)</td>
          </tr>
          <tr style="border-bottom: 1px solid #4a4a4a;">
            <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">PLACE MINE</td>
            <td style="padding: 12px;">Engineers place mine on current tile (5 resources, 50% damage, invisible)</td>
          </tr>
          <tr style="border-bottom: 1px solid #4a4a4a;">
            <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">HEAL Button</td>
            <td style="padding: 12px;">Medics heal adjacent infantry 75% of lost health (costs 50% unit value)</td>
          </tr>
          <tr style="border-bottom: 1px solid #4a4a4a;">
            <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">REPAIR Button</td>
            <td style="padding: 12px;">Engineers repair adjacent vehicles 75% of lost health (costs 50% unit value)</td>
          </tr>
          <tr style="border-bottom: 1px solid #4a4a4a;">
            <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">BOARD Button</td>
            <td style="padding: 12px;">Infantry board adjacent transport vehicles</td>
          </tr>
          <tr>
            <td style="padding: 12px; color: #f0f0f0; font-weight: bold;">DISEMBARK</td>
            <td style="padding: 12px;">Transport unloads carried units to adjacent tiles</td>
          </tr>
        </table>
      </div>
      
      <div style="margin-top: 30px; padding: 15px; background: rgba(255,215,0,0.1); border-left: 4px solid #ffd700; border-radius: 8px;">
        <h4 style="color: #ffd700; margin-bottom: 10px; font-size: 18px;">💡 Essential Control Tips</h4>
        <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.8;">
          <li><strong>Movement & Combat:</strong> Units can move and attack in the same turn, but only attack once</li>
          <li><strong>Movement Continuation:</strong> After attacking, units can continue moving with remaining movement points</li>
          <li><strong>Turn Management:</strong> Click the END TURN button when all units have been moved</li>
          <li><strong>Vision Range:</strong> You can only attack enemies within your unit's vision range</li>
          <li><strong>Counterattacks:</strong> Surviving enemies will counterattack if in range</li>
          <li><strong>City Defense:</strong> Units in friendly cities gain defense bonuses: +30% (Outpost), +40% (Factory), +50% (Airbase)</li>
          <li><strong>Resource Management:</strong> Cities generate 5/10/15 resources per turn based on level</li>
          <li><strong>Veteran Promotion:</strong> Units become veterans (★) after 3 kills, gaining +25% to all stats</li>
          <li><strong>Fog of War:</strong> Use forests and rough terrain to hide units from enemy reconnaissance</li>
          <li><strong>Combined Arms:</strong> Use different unit types together - infantry captures, tanks breakthrough, artillery supports</li>
        </ul>
      </div>
    `;
    return content;
  }

  private createFactionsContent(isMobile: boolean = false): HTMLElement {
    const content = document.createElement('div');
    content.innerHTML = `
      <h3 style="color: #f0f0f0; margin-bottom: 20px; font-size: ${isMobile ? '20px' : '24px'};">Playable Factions</h3>
      
      <div style="display: ${isMobile ? 'block' : 'grid'}; grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'}; gap: 25px;">
        <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <h4 style="color: #4a7c59; margin-bottom: 10px; font-size: 20px;">🇺🇸 United States of America</h4>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Strengths:</strong> Versatile units, strong armor, excellent artillery support
          </p>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Special Units:</strong> Rangers, Airborne, Sherman tanks, P-51 Mustang
          </p>
          <p style="line-height: 1.6;">
            <strong>Campaigns:</strong> Normandy, Okinawa
          </p>
        </div>
        
        <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <h4 style="color: #8b4513; margin-bottom: 10px; font-size: 20px;">🇩🇪 3rd Reich</h4>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Strengths:</strong> Superior tanks, elite infantry, defensive capabilities
          </p>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Special Units:</strong> Waffen-SS, Panthers, 88mm Flak, Bf-109
          </p>
          <p style="line-height: 1.6;">
            <strong>Campaigns:</strong> Normandy, Tunisia, Stalingrad
          </p>
        </div>
        
        <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <h4 style="color: #c41e3a; margin-bottom: 10px; font-size: 20px;">🇬🇧 United Kingdom</h4>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Strengths:</strong> Defensive infantry, reconnaissance, artillery
          </p>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Special Units:</strong> Commandos, Guards, Spitfire, 25-pounder
          </p>
          <p style="line-height: 1.6;">
            <strong>Campaign:</strong> Tunisia
          </p>
        </div>
        
        <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <h4 style="color: #cc0000; margin-bottom: 10px; font-size: 20px;">🇷🇺 USSR</h4>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Strengths:</strong> Mass infantry, heavy tanks, rocket artillery
          </p>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Special Units:</strong> Shock Troops, T-34, KV-1, Katyusha
          </p>
          <p style="line-height: 1.6;">
            <strong>Campaign:</strong> Stalingrad
          </p>
        </div>
        
        <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <h4 style="color: #bc002d; margin-bottom: 10px; font-size: 20px;">🇯🇵 Imperial Japan</h4>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Strengths:</strong> Defensive positions, ambush tactics, fanatical units
          </p>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Special Units:</strong> Cave defenders, Type 97 Chi-Ha, Zero fighter
          </p>
          <p style="line-height: 1.6;">
            <strong>Campaign:</strong> Okinawa
          </p>
        </div>
        
        <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <h4 style="color: #ff0000; margin-bottom: 10px; font-size: 20px;">🇨🇦 Canada</h4>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Strengths:</strong> Balanced forces, Commonwealth support, artillery
          </p>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Special Units:</strong> PPCLI, Highland Regiment, Ram tank
          </p>
          <p style="line-height: 1.6;">
            <strong>Campaign:</strong> Campobasso
          </p>
        </div>
        
        <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <h4 style="color: #009246; margin-bottom: 10px; font-size: 20px;">🇮🇹 Fascist Italy</h4>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Strengths:</strong> Mountain warfare, mobile units, defensive positions
          </p>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Special Units:</strong> Bersaglieri, Alpini, Folgore paratroopers
          </p>
          <p style="line-height: 1.6;">
            <strong>Campaign:</strong> Campobasso
          </p>
        </div>
        
        <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <h4 style="color: #7A9BB8; margin-bottom: 10px; font-size: 20px;">🇫🇷 France</h4>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Strengths:</strong> Heavy armor, defensive doctrine, early war equipment
          </p>
          <p style="margin-bottom: 10px; line-height: 1.6;">
            <strong>Special Units:</strong> Char B1 heavy tanks, AMD Panhard 178, Fusiliers Marins
          </p>
          <p style="line-height: 1.6;">
            <strong>Campaign:</strong> Battle of France
          </p>
        </div>
      </div>
      
      <div style="margin-top: 25px; padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
        <p style="line-height: 1.8;">
          <strong style="color: #f0f0f0;">Note:</strong> Each faction has unique unit compositions and strategies. 
          Experiment with different factions to find your preferred playstyle!
        </p>
      </div>
    `;
    return content;
  }

  private createUnitsContent(isMobile: boolean = false): HTMLElement {
    const content = document.createElement('div');
    content.innerHTML = `
      <h3 style="color: #f0f0f0; margin-bottom: 20px; font-size: ${isMobile ? '20px' : '24px'};">Complete Unit Guide</h3>
      
      <div style="margin-bottom: 25px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">⚔️ Combat Units by Training Level</h4>
        
        <div style="display: ${isMobile ? 'block' : 'grid'}; grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'}; gap: 20px; margin-bottom: 25px;">
          <div style="padding: 15px; background: rgba(122,159,53,0.15); border: 2px solid #7a9f35; border-radius: 8px;">
            <h4 style="color: #7a9f35; margin-bottom: 10px; font-size: 18px;"><img src="/NEWIcons/InfantryColoured.png" style="width: 40px; height: 40px; vertical-align: middle; margin-right: 8px;">Infantry Forces</h4>
            <div style="margin-bottom: 12px;">
              <strong style="color: #f0f0f0;">Standard Infantry (Level 1):</strong> 6-11 resources<br>
              <small>Basic rifle squads, frontline troops, city capture</small>
            </div>
            <div style="margin-bottom: 12px;">
              <strong style="color: #f0f0f0;">Airborne Infantry (Level 2):</strong> 14-16 resources<br>
              <small>Paratroopers, air-mobile operations, behind-enemy-lines</small>
            </div>
            <div style="margin-bottom: 12px;">
              <strong style="color: #f0f0f0;">Special Forces (Level 2-3):</strong> 13-16 resources<br>
              <small>Commandos, elite special operations, specialized missions</small>
            </div>
            <div>
              <strong style="color: #f0f0f0;">Elite Infantry (Level 4):</strong> 16-18 resources<br>
              <small>Rangers, Guards, Waffen-SS - superior stats, veteran formations</small>
            </div>
          </div>
          
          <div style="padding: 15px; background: rgba(139,115,85,0.15); border: 2px solid #8b7355; border-radius: 8px;">
            <h4 style="color: #8b7355; margin-bottom: 10px; font-size: 18px;"><img src="/NEWIcons/ArmourColoured.png" style="width: 40px; height: 40px; vertical-align: middle; margin-right: 8px;">Armored Forces</h4>
            <div style="margin-bottom: 12px;">
              <strong style="color: #f0f0f0;">Light Tanks (Level 1):</strong> 12-25 resources<br>
              <small>T-70, Type 95 Ha-Go - scouting, rapid deployment</small>
            </div>
            <div style="margin-bottom: 12px;">
              <strong style="color: #f0f0f0;">Medium Tanks (Level 2):</strong> 18-28 resources<br>
              <small>Sherman, T-34, Panzer IV - main battle tanks</small>
            </div>
            <div>
              <strong style="color: #f0f0f0;">Heavy Tanks (Level 3):</strong> 24-35 resources<br>
              <small>Tiger I, KV-1, Firefly - breakthrough operations</small>
            </div>
          </div>
          
          <div style="padding: 15px; background: rgba(178,34,34,0.15); border: 2px solid #b22222; border-radius: 8px;">
            <h4 style="color: #b22222; margin-bottom: 10px; font-size: 18px;"><img src="/NEWIcons/ArtilleryColoured.png" style="width: 40px; height: 40px; vertical-align: middle; margin-right: 8px;">Fire Support</h4>
            <div style="margin-bottom: 12px;">
              <strong style="color: #f0f0f0;">Field Artillery (Level 1-2):</strong> 15-28 resources<br>
              <small>M101, 25-pounder, Pak 40 - standard fire support</small>
            </div>
            <div>
              <strong style="color: #f0f0f0;">Heavy Artillery (Level 2-4):</strong> 20-26 resources<br>
              <small>88mm Flak, Katyusha, Type 96 - specialized roles</small>
            </div>
            <p style="margin-top: 10px; font-size: 14px; color: #ccc;">
              <strong>Range:</strong> 4-6 tiles, can attack without retaliation
            </p>
          </div>
          
          <div style="padding: 15px; background: rgba(70,130,180,0.15); border: 2px solid #4682b4; border-radius: 8px;">
            <h4 style="color: #4682b4; margin-bottom: 10px; font-size: 18px;"><img src="/NEWIcons/FighterColoured.png" style="width: 40px; height: 40px; vertical-align: middle; margin-right: 8px;">Air Power</h4>
            <div style="margin-bottom: 12px;">
              <strong style="color: #f0f0f0;">Fighters (Level 2):</strong> 22-32 resources<br>
              <small>P-51, Spitfire, Bf-109 - air superiority, bomber interception</small>
            </div>
            <div>
              <strong style="color: #f0f0f0;">Bombers (Level 3):</strong> 28-38 resources<br>
              <small>Strategic bombing, heavy ground attack, city bombardment</small>
            </div>
            <p style="margin-top: 10px; font-size: 14px; color: #ccc;">
              <strong>Requires:</strong> Level 3 Airbase cities for training
            </p>
          </div>
        </div>
      </div>
      
      <div style="margin-bottom: 25px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">🛠️ Support & Specialist Units</h4>
        
        <div style="display: ${isMobile ? 'block' : 'grid'}; grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'}; gap: 20px;">
          <div style="padding: 15px; background: rgba(212,160,23,0.15); border: 2px solid #d4a017; border-radius: 8px;">
            <h4 style="color: #d4a017; margin-bottom: 10px; font-size: 18px;"><img src="/NEWIcons/EngineerColoured.png" style="width: 40px; height: 40px; vertical-align: middle; margin-right: 8px;">Engineers</h4>
            <div style="margin-bottom: 10px;"><strong>Cost:</strong> 11-15 resources (Level 1)</div>
            <div style="margin-bottom: 10px;"><strong>Special Abilities:</strong></div>
            <ul style="margin-left: 15px; font-size: 14px; line-height: 1.6;">
              <li><strong>Place Mines:</strong> 5 resources, 50% damage, invisible</li>
              <li><strong>Repair Vehicles:</strong> 75% heal, costs 50% of target value</li>
              <li><strong>Hunker Down:</strong> +2 defense, heal 2 HP/turn</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(220,20,60,0.15); border: 2px solid #dc143c; border-radius: 8px;">
            <h4 style="color: #dc143c; margin-bottom: 10px; font-size: 18px;"><img src="/NEWIcons/MedicColoured.png" style="width: 40px; height: 40px; vertical-align: middle; margin-right: 8px;">Medical Units</h4>
            <div style="margin-bottom: 10px;"><strong>Cost:</strong> 10-14 resources (Level 1)</div>
            <div style="margin-bottom: 10px;"><strong>Special Abilities:</strong></div>
            <ul style="margin-left: 15px; font-size: 14px; line-height: 1.6;">
              <li><strong>Heal Infantry:</strong> 75% of lost health to adjacent units</li>
              <li><strong>Cost:</strong> 50% of target's original cost</li>
              <li><strong>Range:</strong> Adjacent tiles only</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(34,139,34,0.15); border: 2px solid #228b22; border-radius: 8px;">
            <h4 style="color: #228b22; margin-bottom: 10px; font-size: 18px;"><img src="/NEWIcons/ReconColoured.png" style="width: 40px; height: 40px; vertical-align: middle; margin-right: 8px;">Reconnaissance</h4>
            <div style="margin-bottom: 10px;"><strong>Cost:</strong> 8-13 resources (Level 1)</div>
            <div style="margin-bottom: 10px;"><strong>Capabilities:</strong></div>
            <ul style="margin-left: 15px; font-size: 14px; line-height: 1.6;">
              <li><strong>High Mobility:</strong> 6-8 movement points</li>
              <li><strong>Extended Vision:</strong> +2 tile sight range</li>
              <li><strong>Light Combat:</strong> Weak but can defend</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(112,128,144,0.15); border: 2px solid #708090; border-radius: 8px;">
            <h4 style="color: #708090; margin-bottom: 10px; font-size: 18px;"><img src="/NEWIcons/TransportColoured.png" style="width: 40px; height: 40px; vertical-align: middle; margin-right: 8px;">Transport Systems</h4>
            <div style="margin-bottom: 10px;"><strong>Cost:</strong> 5-17 resources (Level 1)</div>
            <div style="margin-bottom: 10px;"><strong>Types:</strong></div>
            <ul style="margin-left: 15px; font-size: 14px; line-height: 1.6;">
              <li><strong>Trucks:</strong> Multi-unit capacity, fast deployment</li>
              <li><strong>Halftracks:</strong> Armored, single unit, weapon systems</li>
              <li><strong>Aircraft:</strong> Free with airborne, paradrop capability</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div style="margin-bottom: 25px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">💰 Unit Cost & Training Information</h4>
        
        <div style="padding: 20px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 15px;">
            <div>
              <h5 style="color: #4CAF50; margin-bottom: 8px;">Budget Units (6-12 resources)</h5>
              <ul style="margin-left: 15px; font-size: 14px; line-height: 1.6;">
                <li>Conscripts, Basic Infantry</li>
                <li>Engineers, Reconnaissance</li>
                <li>Medics, Light Vehicles</li>
              </ul>
            </div>
            <div>
              <h5 style="color: #FF9800; margin-bottom: 8px;">Standard Units (13-25 resources)</h5>
              <ul style="margin-left: 15px; font-size: 14px; line-height: 1.6;">
                <li>Most Level 1-2 Infantry</li>
                <li>Medium Tanks, Artillery</li>
                <li>Airborne Units</li>
              </ul>
            </div>
            <div>
              <h5 style="color: #F44336; margin-bottom: 8px;">Premium Units (26-38 resources)</h5>
              <ul style="margin-left: 15px; font-size: 14px; line-height: 1.6;">
                <li>Elite Infantry, Heavy Tanks</li>
                <li>Aircraft, Specialized Artillery</li>
                <li>High-end Equipment</li>
              </ul>
            </div>
          </div>
          
          <div style="margin-top: 20px; padding: 15px; background: rgba(255,215,0,0.1); border-left: 4px solid #ffd700; border-radius: 4px;">
            <h5 style="color: #ffd700; margin-bottom: 8px;">Training Time</h5>
            <ul style="margin-left: 15px; font-size: 14px; line-height: 1.6;">
              <li><strong>1 Turn:</strong> Basic units - immediate deployment</li>
              <li><strong>2 Turns:</strong> Most improved units - requires planning</li>
              <li><strong>3 Turns:</strong> Heavy units - long-term investment</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div style="margin-top: 25px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">⚔️ Advanced Combat Mechanics</h4>
        
        <div style="padding: 20px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <div style="display: ${isMobile ? 'block' : 'grid'}; grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'}; gap: 25px;">
            <div>
              <h5 style="color: #4CAF50; margin-bottom: 10px;">Combat Flow</h5>
              <ul style="margin-left: 15px; list-style-type: disc; line-height: 1.8; font-size: 14px;">
                <li>Units can move and attack in same turn</li>
                <li>Only one attack per turn per unit</li>
                <li>Movement can continue after attacking</li>
                <li>Range determines counterattack capability</li>
                <li>Surviving defenders may counterattack</li>
              </ul>
            </div>
            <div>
              <h5 style="color: #FF9800; margin-bottom: 10px;">Combat Modifiers</h5>
              <ul style="margin-left: 15px; list-style-type: disc; line-height: 1.8; font-size: 14px;">
                <li>Terrain: Easy (0%), Rough (+10%), Challenging (+20%)</li>
                <li>Cities: +30%/+40%/+50% by level</li>
                <li>Veteran status: +25% to all stats</li>
                <li>Health affects combat effectiveness</li>
                <li>Vision range limits attack options</li>
              </ul>
            </div>
          </div>
          
          <div style="margin-top: 20px; padding: 15px; background: rgba(255,215,0,0.1); border-left: 4px solid #ffd700; border-radius: 4px;">
            <h5 style="color: #ffd700; margin-bottom: 8px;">🌟 Veteran System</h5>
            <p style="margin: 0; font-size: 14px; line-height: 1.6;">
              After 3 kills, units become veterans (★) with +25% attack, defense, movement, and health. 
              Veterans are instantly healed to full health on promotion and become immune to shellshock effects.
            </p>
          </div>
        </div>
      </div>
    `;
    return content;
  }

  private createGameModesContent(isMobile: boolean = false): HTMLElement {
    const content = document.createElement('div');
    content.innerHTML = `
      <h3 style="color: #f0f0f0; margin-bottom: 20px; font-size: ${isMobile ? '20px' : '24px'};">Cities & Game Modes</h3>
      
      <div style="margin-bottom: 30px;">
        <h4 style="color: #10b981; margin-bottom: 15px; font-size: 20px;">🏙️ City System</h4>
        
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 20px;">
          <div style="padding: 15px; background: rgba(16, 185, 129, 0.1); border: 2px solid #10b981; border-radius: 8px;">
            <h5 style="color: #10b981; margin-bottom: 10px;">Level 1: Outpost</h5>
            <ul style="list-style: none; padding: 0; font-size: 14px; line-height: 1.8;">
              <li>📦 3 unit capacity</li>
              <li>💰 5 resources/turn</li>
              <li>🛡️ +30% defense bonus</li>
              <li>🚶 Basic infantry/medics/engineers</li>
              <li>✅ Default starting level</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(16, 185, 129, 0.1); border: 2px solid #10b981; border-radius: 8px;">
            <h5 style="color: #10b981; margin-bottom: 10px;">Level 2: Factory</h5>
            <ul style="list-style: none; padding: 0; font-size: 14px; line-height: 1.8;">
              <li>📦 5 unit capacity</li>
              <li>💰 10 resources/turn</li>
              <li>🛡️ +40% defense bonus</li>
              <li>🚗 + Tanks & vehicles</li>
              <li>⬆️ 50 resources, 2 turns</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(16, 185, 129, 0.1); border: 2px solid #10b981; border-radius: 8px;">
            <h5 style="color: #10b981; margin-bottom: 10px;">Level 3: Airbase</h5>
            <ul style="list-style: none; padding: 0; font-size: 14px; line-height: 1.8;">
              <li>📦 8 unit capacity</li>
              <li>💰 15 resources/turn</li>
              <li>🛡️ +50% defense bonus</li>
              <li>✈️ + Aircraft & Airborne</li>
              <li>⬆️ 100 resources, 3 turns</li>
            </ul>
          </div>
        </div>
        
        <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
          <p style="line-height: 1.8; margin-bottom: 10px;">
            <strong style="color: #f0f0f0;">Key Mechanics:</strong>
          </p>
          <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.8;">
            <li>Cities cannot train units while upgrading</li>
            <li>Captured cities retain their level but lose upgrade progress</li>
            <li>City resource generation scales with level: 5/10/15 per turn</li>
            <li>Higher levels unlock new unit types and increase unit capacity</li>
          </ul>
        </div>
      </div>
      
      <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">Game Modes</h4>
      
      <div style="margin-bottom: 25px;">
        <div style="padding: 20px; background: rgba(255,255,255,0.05); border-radius: 8px; margin-bottom: 20px;">
          <h4 style="color: #4a7c59; margin-bottom: 15px; font-size: 20px;">Player vs Player (Local)</h4>
          <p style="line-height: 1.6; margin-bottom: 10px;">
            Two players take turns on the same device. Perfect for local matches with friends.
          </p>
          <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.6;">
            <li>Hot-seat multiplayer</li>
            <li>No AI assistance</li>
            <li>Full fog of war between turns</li>
          </ul>
        </div>
        
        <div style="padding: 20px; background: rgba(255,255,255,0.05); border-radius: 8px; margin-bottom: 20px;">
          <h4 style="color: #b8860b; margin-bottom: 15px; font-size: 20px;">Player vs AI</h4>
          <p style="line-height: 1.6; margin-bottom: 10px;">
            Battle against computer-controlled opponents with adaptive AI strategies.
          </p>
          <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.6;">
            <li>AI adapts to player strategies</li>
            <li>Different difficulty based on faction matchup</li>
            <li>Perfect for practice and solo play</li>
          </ul>
        </div>
        
        <div style="padding: 20px; background: rgba(255,255,255,0.05); border-radius: 8px; margin-bottom: 20px;">
          <h4 style="color: #4682b4; margin-bottom: 15px; font-size: 20px;">Online Multiplayer</h4>
          <p style="line-height: 1.6; margin-bottom: 10px;">
            Play against opponents worldwide through online matchmaking.
          </p>
          <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.6;">
            <li>Real-time synchronization</li>
            <li>Automatic reconnection support</li>
            <li>Save and resume games</li>
          </ul>
        </div>
      </div>
      
      <div style="padding: 20px; background: rgba(255,255,255,0.05); border-radius: 8px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">Victory Conditions</h4>
        <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.8;">
          <li><strong>Total Victory:</strong> Capture all cities on the map</li>
          <li><strong>Resignation:</strong> Opponent surrenders</li>
        </ul>
        
        <p style="margin-top: 15px; line-height: 1.6;">
          <strong style="color: #f0f0f0;">Tip:</strong> Cities are key to victory - they provide resources for new units and serve as defensive strongholds.
        </p>
      </div>
      
      <div style="margin-top: 20px; padding: 20px; background: rgba(255,255,255,0.05); border-radius: 8px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">Save System</h4>
        <p style="line-height: 1.6;">
          Games are automatically saved after each turn. You can have up to 5 save slots for different games. 
          Continue your campaigns anytime from the main menu. Online games have a separate save slot.
        </p>
      </div>
    `;
    return content;
  }

  private createTerrainContent(isMobile: boolean = false): HTMLElement {
    const content = document.createElement('div');
    content.innerHTML = `
      <h3 style="color: #f0f0f0; margin-bottom: 20px; font-size: ${isMobile ? '20px' : '24px'};">Terrain System</h3>
      
      <div style="margin-bottom: 25px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">Core Terrain Types</h4>
        <p style="line-height: 1.8; margin-bottom: 15px;">
          All maps use a three-tier terrain system that affects movement costs and provides defensive bonuses:
        </p>
        
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 20px;">
          <div style="padding: 15px; background: rgba(46, 125, 50, 0.2); border: 2px solid #4CAF50; border-radius: 8px;">
            <h5 style="color: #4CAF50; margin-bottom: 10px; font-size: 16px;">Easy Terrain (E)</h5>
            <ul style="list-style: none; padding: 0; font-size: 14px; line-height: 1.6;">
              <li>🚶 <strong>Movement Cost:</strong> 1 point</li>
              <li>⚔️ <strong>Combat:</strong> No modifiers</li>
              <li>👁️ <strong>Visibility:</strong> Clear sight lines</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(255, 152, 0, 0.2); border: 2px solid #FF9800; border-radius: 8px;">
            <h5 style="color: #FF9800; margin-bottom: 10px; font-size: 16px;">Rough Terrain (R)</h5>
            <ul style="list-style: none; padding: 0; font-size: 14px; line-height: 1.6;">
              <li>🚶 <strong>Movement Cost:</strong> 2 points</li>
              <li>🛡️ <strong>Defense:</strong> Moderate bonuses</li>
              <li>🌿 <strong>Cover:</strong> Partial concealment</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(244, 67, 54, 0.2); border: 2px solid #F44336; border-radius: 8px;">
            <h5 style="color: #F44336; margin-bottom: 10px; font-size: 16px;">Challenging Terrain (C)</h5>
            <ul style="list-style: none; padding: 0; font-size: 14px; line-height: 1.6;">
              <li>🚶 <strong>Movement Cost:</strong> 3 points</li>
              <li>🛡️ <strong>Defense:</strong> High bonuses</li>
              <li>🌲 <strong>Cover:</strong> Excellent concealment</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div style="margin-bottom: 25px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">Campaign-Specific Terrain</h4>
        <p style="line-height: 1.8; margin-bottom: 15px;">
          Each campaign features unique visual terrain types that map to the E/R/C system:
        </p>
        
        <div style="display: ${isMobile ? 'block' : 'grid'}; grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'}; gap: 15px;">
          <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h5 style="color: #7a9f35; margin-bottom: 10px;">🌾 Normandy (1944)</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li><strong>Easy:</strong> Open Fields</li>
              <li><strong>Rough:</strong> Bocage (hedgerows)</li>
              <li><strong>Challenging:</strong> Dense Woods</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h5 style="color: #d2691e; margin-bottom: 10px;">🏜️ Tunisia (1943)</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li><strong>Easy:</strong> Desert Plains</li>
              <li><strong>Rough:</strong> Rocky Highlands</li>
              <li><strong>Challenging:</strong> Shifting Dunes</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h5 style="color: #4682b4; margin-bottom: 10px;">❄️ Stalingrad (1942)</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li><strong>Easy:</strong> Frozen Steppe</li>
              <li><strong>Rough:</strong> Snow Drifts</li>
              <li><strong>Challenging:</strong> Frozen Marshes</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h5 style="color: #228b22; margin-bottom: 10px;">🌺 Okinawa (1945)</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li><strong>Easy:</strong> Rice Paddies</li>
              <li><strong>Rough:</strong> Coral Ridges</li>
              <li><strong>Challenging:</strong> Dense Jungle</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h5 style="color: #8b4513; margin-bottom: 10px;">🏔️ Campobasso (1943)</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li><strong>Easy:</strong> Valley Floors</li>
              <li><strong>Rough:</strong> Wooded Hills</li>
              <li><strong>Challenging:</strong> Mountain Ridges</li>
            </ul>
          </div>
          
          <div style="padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h5 style="color: #7A9BB8; margin-bottom: 10px;">🇫🇷 Battle of France (1940)</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li><strong>Easy:</strong> French Countryside</li>
              <li><strong>Rough:</strong> Rolling Hills</li>
              <li><strong>Challenging:</strong> Dense Forests</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div style="margin-top: 25px; padding: 20px; background: rgba(255,255,255,0.05); border-radius: 8px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">Tactical Applications</h4>
        <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.8;">
          <li><strong>Movement Planning:</strong> Use Easy terrain for rapid advances and repositioning</li>
          <li><strong>Defensive Positioning:</strong> Place units on Rough/Challenging terrain before enemy attacks</li>
          <li><strong>Attack Routing:</strong> Force enemies to attack through difficult terrain</li>
          <li><strong>Reconnaissance:</strong> Use scouts to identify optimal movement paths</li>
          <li><strong>Artillery Placement:</strong> Position long-range units behind terrain obstacles</li>
          <li><strong>City Defense:</strong> Cities provide additional bonuses (+30%/+40%/+50% by level)</li>
        </ul>
      </div>
    `;
    return content;
  }

  private createStrategyContent(isMobile: boolean = false): HTMLElement {
    const content = document.createElement('div');
    content.innerHTML = `
      <h3 style="color: #f0f0f0; margin-bottom: 20px; font-size: ${isMobile ? '20px' : '24px'};">Strategic Guide</h3>
      
      <div style="margin-bottom: 25px;">
        <h4 style="color: #4CAF50; margin-bottom: 15px; font-size: 20px;">🚀 Early Game Strategy (Turns 1-8)</h4>
        <div style="padding: 20px; background: rgba(76, 175, 80, 0.1); border-left: 4px solid #4CAF50; border-radius: 8px;">
          <h5 style="color: #4CAF50; margin-bottom: 10px;">Priority: Secure Economic Base</h5>
          <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.8;">
            <li><strong>Capture Neutral Cities:</strong> Send fast units to undefended cities immediately</li>
            <li><strong>Scout the Map:</strong> Use reconnaissance units to find all cities and enemy positions</li>
            <li><strong>Build Essential Units:</strong> Focus on infantry, engineers, and medics first</li>
            <li><strong>Defensive Setup:</strong> Position starting units on defensive terrain</li>
            <li><strong>Resource Management:</strong> Don't overspend - you need resources for expansion</li>
          </ul>
          
          <div style="margin-top: 15px; padding: 15px; background: rgba(255,255,255,0.05); border-radius: 5px;">
            <strong style="color: #4CAF50;">Pro Tip:</strong> The faction that captures more cities in the first 5 turns usually has a significant advantage throughout the game.
          </div>
        </div>
      </div>
      
      <div style="margin-bottom: 25px;">
        <h4 style="color: #FF9800; margin-bottom: 15px; font-size: 20px;">⚔️ Mid Game Strategy (Turns 9-20)</h4>
        <div style="padding: 20px; background: rgba(255, 152, 0, 0.1); border-left: 4px solid #FF9800; border-radius: 8px;">
          <h5 style="color: #FF9800; margin-bottom: 10px;">Priority: Build Military Strength</h5>
          <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.8;">
            <li><strong>Upgrade Cities:</strong> Start upgrading your safest cities to Factory level</li>
            <li><strong>Combined Arms:</strong> Build balanced forces (infantry + armor + artillery)</li>
            <li><strong>Veteran Development:</strong> Protect units approaching veteran status (2 kills)</li>
            <li><strong>Engineer Operations:</strong> Use mines for defensive lines, repair valuable units</li>
            <li><strong>Medical Support:</strong> Keep medics alive to extend your army's effectiveness</li>
            <li><strong>Territory Control:</strong> Secure key terrain features and chokepoints</li>
          </ul>
          
          <div style="margin-top: 15px; padding: 15px; background: rgba(255,255,255,0.05); border-radius: 5px;">
            <strong style="color: #FF9800;">Key Milestone:</strong> Having at least one Factory-level city unlocks tanks and artillery - essential for mid-game dominance.
          </div>
        </div>
      </div>
      
      <div style="margin-bottom: 25px;">
        <h4 style="color: #F44336; margin-bottom: 15px; font-size: 20px;">🏆 Late Game Strategy (Turns 21+)</h4>
        <div style="padding: 20px; background: rgba(244, 67, 54, 0.1); border-left: 4px solid #F44336; border-radius: 8px;">
          <h5 style="color: #F44336; margin-bottom: 10px;">Priority: Decisive Operations</h5>
          <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.8;">
            <li><strong>Air Superiority:</strong> Build Airbase cities for aircraft and air support</li>
            <li><strong>Veteran Spearheads:</strong> Use veteran units to lead assaults on enemy cities</li>
            <li><strong>Multi-Front Attacks:</strong> Coordinate simultaneous attacks from multiple directions</li>
            <li><strong>Resource Warfare:</strong> Target enemy economic centers to cripple their production</li>
            <li><strong>Logistics:</strong> Use transports to rapidly redeploy forces where needed</li>
          </ul>
          
          <div style="margin-top: 15px; padding: 15px; background: rgba(255,255,255,0.05); border-radius: 5px;">
            <strong style="color: #F44336;">Victory Focus:</strong> Don't just destroy enemy units - capture their cities to prevent reinforcements and secure total victory.
          </div>
        </div>
      </div>
      
      <div style="margin-bottom: 25px;">
        <h4 style="color: #9C27B0; margin-bottom: 15px; font-size: 20px;">⭐ Veteran System Mastery</h4>
        <div style="padding: 20px; background: rgba(156, 39, 176, 0.1); border-left: 4px solid #9C27B0; border-radius: 8px;">
          <ul style="margin-left: 20px; list-style-type: disc; line-height: 1.8;">
            <li><strong>Kill Farming:</strong> Use weak enemy units to promote your valuable units to veteran status</li>
            <li><strong>Protection Priority:</strong> Units with 2 kills are extremely valuable - keep them safe</li>
            <li><strong>Veteran Roles:</strong> Use veterans for city assaults and critical defensive positions</li>
            <li><strong>Stats Advantage:</strong> Veterans get +25% to ALL stats - they're worth multiple regular units</li>
            <li><strong>Healing Bonus:</strong> Veterans are immune to shellshock and get full healing on promotion</li>
          </ul>
        </div>
      </div>
      
      <div style="margin-top: 25px; padding: 20px; background: rgba(255,255,255,0.05); border-radius: 8px;">
        <h4 style="color: #f0f0f0; margin-bottom: 15px; font-size: 20px;">🎯 Universal Principles</h4>
        <div style="display: ${isMobile ? 'block' : 'grid'}; grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'}; gap: 20px;">
          <div>
            <h5 style="color: #4CAF50; margin-bottom: 10px;">Economic Priorities</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li>Cities > unit kills for victory</li>
              <li>Upgrade timing is critical</li>
              <li>Higher level cities = more resources</li>
              <li>Protect your economic base</li>
            </ul>
          </div>
          
          <div>
            <h5 style="color: #FF9800; margin-bottom: 10px;">Combat Tactics</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li>Concentrate force against weakness</li>
              <li>Use terrain for defensive advantage</li>
              <li>Combined arms beat single unit types</li>
              <li>Veterans are force multipliers</li>
            </ul>
          </div>
          
          <div>
            <h5 style="color: #2196F3; margin-bottom: 10px;">Strategic Thinking</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li>Plan 3-5 turns ahead</li>
              <li>Control key terrain features</li>
              <li>Force enemies into bad positions</li>
              <li>Always have backup plans</li>
            </ul>
          </div>
          
          <div>
            <h5 style="color: #9C27B0; margin-bottom: 10px;">Common Mistakes</h5>
            <ul style="margin-left: 15px; line-height: 1.6; font-size: 14px;">
              <li>Spreading forces too thin</li>
              <li>Neglecting city upgrades</li>
              <li>Ignoring veteran development</li>
              <li>Poor resource management</li>
            </ul>
          </div>
        </div>
      </div>
    `;
    return content;
  }
}